import { useEffect, useMemo, useState, type ReactNode } from 'react';

import { CircleCheck, CircleDashed, CircleX, CornerDownRight, File, Globe } from 'lucide-react';

import { CARE_STATE_NOTE_TAB_ANCHOR } from '@/app/hints';
import {
  CARE_STATE_SCOPE_LABEL,
  type ClinicalScopeT,
  useCareStateCommands,
  useCareStateDomain,
} from '@/entities/care-state-domain';
import {
  AgentTweakableArea,
  TextFieldTool,
  useAgentTweakable,
} from '@/features/copilot/agent-tweakable';
import { formatLotusUuidDateTime } from '@/shared/lib';
import { LexicalMarkdownEditor, useHintAnchor } from '@/shared/ui/primitives';
import { Badge } from '@/shared/ui/shadcn/badge';
import { Button } from '@/shared/ui/shadcn/button';
import { focusFollowUpInTab } from '@/shared/stores/ui';

export type CareStateNoteTabProps = {
  patientUserId: string;
  /**
   * The scope this tab renders. Provided explicitly by the caller (e.g. the
   * workspace tab registry) so a single component can power both the static
   * Overview tab and the dynamic Problem/Request tab without coupling tab
   * selection to the global clinical scope.
   */
  scope: ClinicalScopeT;
};

function CareStateNoteTabAgentBindings(props: {
  draftText: string;
  onSaveText: (next: string) => { saved: boolean; reason?: string };
}) {
  const { draftText, onSaveText } = props;

  useAgentTweakable(TextFieldTool, 'careNote', draftText, (next: string) => {
    return onSaveText(next);
  });

  return null;
}

function getFollowUpStatusDotClass(status: string | undefined): string {
  switch (status) {
    case 'Pending':
      return 'bg-muted-foreground/40';
    case 'Awaiting Response from Patient':
      return 'bg-yellow-500';
    case 'Response Received from Patient':
      return 'bg-green-500';
    default:
      return 'bg-muted-foreground/30';
  }
}

export function CareStateNoteTab({ patientUserId, scope }: CareStateNoteTabProps) {
  const { careState } = useCareStateDomain();
  const commands = useCareStateCommands();
  const noteAnchorRef = useHintAnchor(CARE_STATE_NOTE_TAB_ANCHOR);
  const linkedRequestFollowUps = useMemo(() => {
    if (careState === null || scope.type !== 'request') {
      return [];
    }
    return careState.follow_ups.filter((followUp) => {
      return followUp.link.type === 'request' && followUp.link.id === scope.id;
    });
  }, [careState, scope]);

  const { scopeKey, title, titleIcon, subtitle, currentNote, canSave, save } = useMemo<{
    scopeKey: string;
    title: string;
    titleIcon: ReactNode;
    subtitle: string | null;
    currentNote: string;
    canSave: boolean;
    save: (note: string, actor?: 'clinician' | 'copilot') => void;
  }>(() => {
    if (careState === null) {
      return {
        scopeKey: 'loading',
        title: 'Loading…',
        titleIcon: null,
        subtitle: null,
        currentNote: '',
        canSave: false,
        save: () => undefined,
      };
    }

    switch (scope.type) {
      case 'patient': {
        const patient = careState.patients.find((p) => p.id === 'root') ?? null;
        return {
          scopeKey: 'patient:root',
          title: CARE_STATE_SCOPE_LABEL.patient,
          titleIcon: <Globe className="h-5 w-5 shrink-0 text-muted-foreground" />,
          subtitle: null,
          currentNote: patient?.note.value ?? '',
          canSave: true,
          save: (note: string, actor: 'clinician' | 'copilot' = 'clinician') =>
            commands.execute({ type: 'patient.setNote', note }, { actor }),
        };
      }
      case 'problem': {
        const problem = careState.problems.find((p) => p.id === scope.id) ?? null;
        return {
          scopeKey: `problem:${scope.id}`,
          title: problem?.title ?? CARE_STATE_SCOPE_LABEL.problem,
          titleIcon: <File className="h-5 w-5 shrink-0 text-muted-foreground" />,
          subtitle: (() => {
            const createdAtLabel = formatLotusUuidDateTime(scope.id);
            return createdAtLabel ? `Created ${createdAtLabel}` : null;
          })(),
          currentNote: problem?.note.value ?? '',
          canSave: problem !== null,
          save: (note: string, actor: 'clinician' | 'copilot' = 'clinician') =>
            commands.execute({ type: 'problem.setNote', problemId: scope.id, note }, { actor }),
        };
      }
      case 'request': {
        const request = careState.requests.find((r) => r.id === scope.id) ?? null;
        const statusIcon = (() => {
          switch (request?.status) {
            case 'Open':
              return (
                <CircleDashed
                  className="h-5 w-5 shrink-0 text-muted-foreground/70"
                  strokeWidth={2.5}
                />
              );
            case 'Resolved':
              return <CircleCheck className="h-5 w-5 shrink-0 text-green-700" strokeWidth={2.5} />;
            case 'Cancelled':
              return <CircleX className="h-5 w-5 shrink-0 text-red-700" strokeWidth={2.5} />;
            default:
              return (
                <CircleDashed
                  className="h-5 w-5 shrink-0 text-muted-foreground/70"
                  strokeWidth={2.5}
                />
              );
          }
        })();
        return {
          scopeKey: `request:${scope.id}`,
          title: request?.title ?? CARE_STATE_SCOPE_LABEL.request,
          titleIcon: statusIcon,
          subtitle: (() => {
            const createdAtLabel = formatLotusUuidDateTime(scope.id);
            return createdAtLabel ? `Created ${createdAtLabel}` : null;
          })(),
          currentNote: request?.note.value ?? '',
          canSave: request !== null,
          save: (note: string, actor: 'clinician' | 'copilot' = 'clinician') =>
            commands.execute({ type: 'request.setNote', requestId: scope.id, note }, { actor }),
        };
      }
      default: {
        const _exhaustive: never = scope;
        return _exhaustive;
      }
    }
  }, [careState, commands, scope]);

  const resetKey = `${patientUserId}:${scopeKey}`;

  // Base note reflects the last saved value for this scope.
  const [baseText, setBaseText] = useState('');
  // Draft is user-editable; we only overwrite it automatically if not dirty.
  const [draftText, setDraftText] = useState('');

  // Reset when scope changes.
  useEffect(() => {
    setBaseText(currentNote);
    setDraftText(currentNote);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  // If the underlying note changes for the same scope and we aren't dirty, keep the editor in sync.
  useEffect(() => {
    if (draftText !== baseText) return;
    if (baseText === currentNote) return;
    setBaseText(currentNote);
    setDraftText(currentNote);
  }, [baseText, currentNote, draftText]);

  const isDirty = draftText !== baseText;

  const handleDiscard = () => {
    setDraftText(baseText);
  };

  const handleSave = () => {
    save(draftText, 'clinician');
    setBaseText(draftText);
    setDraftText(draftText);
  };

  const handleSaveText = (next: string) => {
    if (!canSave) return { saved: false, reason: 'cannotSave' };
    save(next, 'copilot');
    setBaseText(next);
    setDraftText(next);
    return { saved: true };
  };

  return (
    <AgentTweakableArea prefix="careStateNoteTab">
      <CareStateNoteTabAgentBindings draftText={draftText} onSaveText={handleSaveText} />

      <div ref={noteAnchorRef} className="flex h-full min-h-0 flex-col gap-2">
        <div className="flex flex-col gap-1">
          <div className="flex items-center justify-between gap-2">
            <div className="flex min-w-0 items-center gap-2 truncate">
              {titleIcon}
              <div className="min-w-0 truncate text-xl font-regular text-foreground">{title}</div>
            </div>

            {isDirty ? (
              <div className="flex items-center gap-1.5">
                <Button type="button" variant="secondary" size="chip" onClick={handleDiscard}>
                  Discard
                </Button>
                <Button type="button" size="chip" disabled={!canSave} onClick={handleSave}>
                  Save
                </Button>
              </div>
            ) : null}
          </div>

          {subtitle ? (
            <Badge variant="secondary" className="self-start rounded-full text-xs">
              {subtitle}
            </Badge>
          ) : null}
        </div>

        <div className="min-h-0 flex-1 overflow-hidden rounded-md bg-background">
          {scope.type === 'request' && linkedRequestFollowUps.length > 0 ? (
            <div className="mb-1 border-l-2 border-muted-foreground/20 ml-1">
              {linkedRequestFollowUps.map((followUp) => {
                const createdAtLabel = formatLotusUuidDateTime(followUp.id);
                const statusDotClass = getFollowUpStatusDotClass(followUp.status);
                return (
                  <button
                    key={followUp.id}
                    type="button"
                    className="group flex w-full items-start gap-2 px-3 py-1.5 text-left transition-colors hover:bg-muted/50"
                    onClick={() => {
                      focusFollowUpInTab(followUp.id);
                    }}
                    aria-label={`Open follow-up ${followUp.title} in Follow-ups overlay`}
                  >
                    <CornerDownRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
                    <div className="flex min-w-0 flex-1 items-baseline gap-1.5">
                      <span
                        className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${statusDotClass}`}
                      />
                      <span className="truncate text-sm text-foreground/80 group-hover:text-foreground">
                        {followUp.title}
                      </span>
                      {createdAtLabel !== null ? (
                        <span className="shrink-0 text-xs text-muted-foreground">
                          {createdAtLabel}
                        </span>
                      ) : null}
                    </div>
                  </button>
                );
              })}
            </div>
          ) : null}
          <LexicalMarkdownEditor
            key={resetKey}
            value={draftText}
            onChange={setDraftText}
            className="rounded-none border-0 bg-transparent p-0 focus-within:ring-0 focus-within:ring-offset-0"
            placeholder="Write a note…"
            toolbar="none"
          />
        </div>
      </div>
    </AgentTweakableArea>
  );
}
